import React from 'react';
import { Box, Grid2, Link, Typography, Container, Paper, Button, useTheme, Icon } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const PoliticasPrivacidad = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleVolverInicio = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sections = [
    {
      id: "responsable",
      title: "1. Responsable del tratamiento de los datos",
      icon: "business",
      content: (
        <>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            La entidad responsable del tratamiento de los datos recopilados a través de <strong>Mi Rifa App</strong> es:
          </Typography>
          <Box
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
              borderRadius: '10px',
              p: 2.5,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            }}
          >
            <Typography sx={{ mb: 1, fontWeight: 800, color: theme.palette.text.primary }}>
              RAUH TECH Limitada
            </Typography>
            <Typography sx={{ mb: 1, color: theme.palette.text.secondary, fontSize: '0.95rem' }}>
              Dirección: Carrera 14 #98-73 Oficina 101. Bogotá – 110221. Colombia.
            </Typography>
            <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.95rem' }}>
              Correo electrónico:{' '}
              <Link href="mailto:administration@rauhtech.com" sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}>
                administration@rauhtech.com
              </Link>
            </Typography>
          </Box>
        </>
      )
    },
    {
      id: "datos",
      title: "2. Datos personales que recopilamos",
      icon: "badge",
      content: (
        <>
          <Typography sx={{ mb: 2.5, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            <strong>Mi Rifa App</strong> recopila datos personales de los usuarios con el propósito de gestionar su experiencia en la plataforma. Los datos que recopilamos incluyen:
          </Typography>
          <Grid2 container spacing={2}>
            {[
              { title: "Datos de Identidad", desc: "Nombre y apellido del usuario o participante." },
              { title: "Datos de Contacto", desc: "Dirección de correo electrónico y número de teléfono móvil." },
              { title: "Información de Compra", desc: "Al adquirir o reservar un boleto, se registran nombre, apellido, correo electrónico y número de teléfono del comprador." }
            ].map((item, index) => (
              <Grid2 size={{ xs: 12, md: 4 }} key={index}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                    height: '100%'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, mb: 0.8 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: '10px',
              bgcolor: 'rgba(241, 91, 40, 0.08)',
              border: '1px solid rgba(241, 91, 40, 0.25)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
              Nota importante sobre pagos:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, lineHeight: 1.6 }}>
              No recopilamos información financiera sensible, como números de tarjetas de crédito o datos bancarios, ya que no procesamos pagos directamente en la plataforma.
            </Typography>
          </Box>
        </>
      )
    },
    {
      id: "finalidad",
      title: "3. Finalidad del tratamiento de los datos",
      icon: "track_changes",
      content: (
        <>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Los datos personales que recopilamos se utilizan para los siguientes fines:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {[
              "Crear y gestionar cuentas de usuario en la aplicación.",
              "Administrar la compra, reserva y pago de boletas dentro de las rifas organizadas.",
              "Facilitar la comunicación entre los organizadores de las rifas y los compradores de boletas.",
              "Mejorar el funcionamiento y la seguridad de la aplicación.",
              "Cumplir con las obligaciones legales aplicables."
            ].map((text, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '8px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)', color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 900, flexShrink: 0 }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )
    },
    {
      id: "derechos",
      title: "4. Derechos del usuario",
      icon: "verified_user",
      content: (
        <>
          <Typography sx={{ mb: 2.5, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Como titular de los datos personales, usted tiene los siguientes derechos:
          </Typography>
          <Grid2 container spacing={2}>
            {[
              { title: "Acceso", desc: "Solicitar una copia de los datos personales que tenemos sobre usted." },
              { title: "Rectificación", desc: "Solicitar la corrección de datos personales inexactos o incompletos." },
              { title: "Supresión", desc: "Solicitar la eliminación de sus datos personales cuando ya no sean necesarios para los fines para los que fueron recopilados." },
              { title: "Oposición y Limitación", desc: "Oponerse al tratamiento de sus datos o solicitar la limitación del mismo bajo ciertas circunstancias." }
            ].map((item, index) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={index}>
                <Box sx={{ p: 2, borderRadius: '10px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
          <Box sx={{ mt: 2.5, p: 2, borderRadius: '10px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
              Para ejercer cualquiera de estos derechos, puede ponerse en contacto con nosotros a través del correo electrónico{' '}
              <Link href="mailto:administration@rauhtech.com" sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontWeight: 700, textDecoration: 'none' }}>
                administration@rauhtech.com
              </Link>.
            </Typography>
          </Box>
        </>
      )
    },
    {
      id: "seguridad",
      title: "5. Seguridad de los datos",
      icon: "security",
      content: (
        <Box sx={{ p: 2.5, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc', borderRadius: '10px', border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            En <strong>RAUH TECH LTDA</strong>, implementamos medidas de seguridad robustas para proteger la información personal de los usuarios. Esto incluye el uso de protocolos avanzados de encriptación y tecnologías de seguridad que garantizan que los datos se almacenen de manera segura y sólo sean accesibles por personal autorizado.
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
            Además, revisamos y actualizamos regularmente nuestras prácticas de seguridad para mantenernos al día con las mejores prácticas y proteger los datos contra accesos no autorizados, alteraciones o divulgaciones.
          </Typography>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
      {/* Hero Header Corporativo */}
      <Box
        sx={{
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(145deg, #1c215e 0%, #2d3484 100%)',
          color: 'white',
          pt: { xs: 12, md: 15 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 220,
            height: 220,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            top: -50,
            right: 40,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            bottom: -40,
            left: '20%',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            onClick={handleVolverInicio}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{
              color: 'white',
              fontWeight: 700,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              mb: 3,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
            }}
          >
            Volver al Inicio
          </Button>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '2.8rem' },
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Políticas de Privacidad
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1.05rem', mb: 2 }}>
            Mi Rifa App • RAUH TECH LTDA
          </Typography>

          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '6px' }}>
            <Icon sx={{ fontSize: 16, color: '#10b981' }}>schedule</Icon>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)' }}>
              Última actualización: 19 de septiembre de 2024
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contenido Principal */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Tarjeta de Introducción */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: '12px',
            bgcolor: theme.palette.background.paper,
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 10px rgba(0, 0, 0, 0.03)',
          }}
        >
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, mb: 2, fontSize: '1rem' }}>
            En <strong>RAUH TECH LTDA</strong>, respetamos su privacidad y nos comprometemos a proteger la información personal que nos proporciona a través de nuestra aplicación, <strong>Mi Rifa App</strong>. Esta Política de Privacidad describe cómo recopilamos, utilizamos, compartimos y protegemos sus datos personales, de acuerdo con las normativas aplicables sobre protección de datos y privacidad, incluyendo el Reglamento General de Protección de Datos (RGPD) y otras normativas locales.
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, fontSize: '1rem' }}>
            Al utilizar nuestra aplicación, usted acepta las prácticas descritas en esta Política de Privacidad. Si no está de acuerdo con los términos aquí expuestos, le rogamos no utilice nuestra aplicación.
          </Typography>
        </Paper>

        {/* Secciones */}
        {sections.map((section) => (
          <Paper
            key={section.id}
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3.5,
              borderRadius: '12px',
              bgcolor: theme.palette.background.paper,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 2px 10px rgba(0, 0, 0, 0.03)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              {/* Outlined Icon Box */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 24, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>{section.icon}</Icon>
              </Box>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  fontSize: { xs: '1.15rem', md: '1.3rem' },
                }}
              >
                {section.title}
              </Typography>
            </Box>

            {section.content}
          </Paper>
        ))}
      </Container>
    </Box>
  );
};

export default PoliticasPrivacidad;